/**
 * Standard API Response Format
 * - If data exists, error is null
 * - If error exists, data is null
 * - status always present
 * - message optional (from API response)
 */
export interface StandardResponse<T = any> {
  data: T | null;
  error: any | null;
  status: number | string;
  message?: string;
}

/**
 * API Response Shape for axios responses
 * Represents the raw API response before StandardResponse wrapping
 */
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  errors?: any;
}

/**
 * Extracts the most relevant error message with priority:
 * 1. Validation errors from error.response.data.errors (field-specific validation errors)
 * 2. error.response.data.message (API-provided message for actionable feedback)
 * 3. error.response.data.error (alternative API error field)
 * 4. error.response.statusText (HTTP status text)
 * 5. error.message (error object message)
 * 6. Generic fallback message
 */
export const extractErrorMessage = (error: any): string => {
  try {
    // Priority 1: Validation errors (e.g., { errors: { field: ['error message'] } })
    // Check this FIRST before error.message, as validation errors are more specific
    if (
      error?.response?.data?.errors &&
      typeof error.response.data.errors === "object"
    ) {
      const errorMessages: string[] = [];
      Object.keys(error.response.data.errors).forEach((key) => {
        const fieldErrors = error.response.data.errors[key];
        if (Array.isArray(fieldErrors)) {
          errorMessages.push(...fieldErrors);
        }
      });
      if (errorMessages.length > 0) {
        return errorMessages.join(", ");
      }
    }

    // Priority 2: API-provided message in response data
    if (
      error?.response?.data?.message &&
      typeof error.response.data.message === "string"
    ) {
      return error.response.data.message;
    }

    // Priority 3: API error field (e.g., {"error":"Unknown error occurred"})
    if (
      error?.response?.data?.error &&
      typeof error.response.data.error === "string"
    ) {
      return error.response.data.error;
    }

    // Priority 4: Fallback to statusText
    if (
      error?.response?.statusText &&
      typeof error.response.statusText === "string"
    ) {
      return error.response.statusText;
    }

    // Priority 5: Error message property
    if (error?.message && typeof error.message === "string") {
      return error.message;
    }

    // Priority 6: Try to stringify the error response data as fallback
    if (error?.response?.data && typeof error.response.data === "string") {
      return error.response.data;
    }

    // Fallback for completely unknown errors
    return "An error occurred. Please try again.";
  } catch (err) {
    // Last resort fallback
    return "An unexpected error occurred.";
  }
};

/**
 * Processes API response in the standard format (data, error, status)
 * Handles both standard and non-standard responses with fallback
 */
export const handleApiResponse = <T = any>(
  response: any,
): StandardResponse<T> => {
  try {
    // If response is already in standard format
    if (
      typeof response === "object" &&
      response !== null &&
      "status" in response &&
      ("data" in response || "error" in response)
    ) {
      const { data, error, status } = response;

      // Validate standard format: data or error should be present
      if (data !== null || error !== null) {
        return {
          data: data ?? null,
          error: error ?? null,
          status: status,
        };
      }
    }

    // Handle non-standard response - build error from response structure
    const standardizedResponse: StandardResponse<T> = {
      data: null,
      error: null,
      status: response?.status || response?.statusCode || 500,
    };

    // Try to extract data from common response structures
    if (
      response?.data &&
      (response.status === 200 || response.status === 201)
    ) {
      return {
        data: response.data,
        error: null,
        status: response.status,
      };
    }

    // If we get here, treat it as an error response
    const errorMessage = extractErrorMessage(response);
    standardizedResponse.error = {
      message: errorMessage,
      response: response,
    };

    return standardizedResponse;
  } catch (err) {
    // Fallback for unparseable responses
    return {
      data: null,
      error: {
        message: "Failed to process server response",
        details: err,
      },
      status: 500,
      message: "Failed to process server response",
    };
  }
};

/**
 * Handles API errors from axios/fetch and transforms them to standard format
 * Useful for catch blocks in API calls
 */
export const handleApiError = (error: any): StandardResponse => {
  try {
    const status = error?.response?.status || error?.status || 500;
    const errorMessage = extractErrorMessage(error);

    return {
      data: null,
      error: {
        message: errorMessage,
        status: status,
        details: error?.response?.data,
      },
      status: status,
      message: errorMessage,
    };
  } catch (err) {
    return {
      data: null,
      error: {
        message: "An unexpected error occurred",
        details: err,
      },
      status: 500,
      message: "An unexpected error occurred",
    };
  }
};

/**
 * Handles successful API responses and transforms them to standard format
 * Use this in thunk success paths instead of manually constructing responses
 *
 * @example
 * const response = await axios.get(...);
 * return handleApiSuccess(response.data);
 */
export const handleApiSuccess = <T = any>(
  apiResponse: any,
  status: number = 200,
): StandardResponse<T> => {
  try {
    // If the API response already has the expected structure with data and message
    if (apiResponse && typeof apiResponse === "object") {
      return {
        data: apiResponse.data ?? null,
        error: null,
        status: status,
        message: apiResponse.message,
      };
    }

    // Fallback for unexpected response shapes
    return {
      data: apiResponse ?? null,
      error: null,
      status: status,
      message: apiResponse?.message ?? null,
    };
  } catch (err) {
    return {
      data: null,
      error: {
        message: "Failed to process success response",
        details: err,
      },
      status: 500,
      message: "Failed to process success response",
    };
  }
};

/**
 * Validates if a response is in the expected standard format
 * Enforces the invariant: exactly one of data or error must be null (XOR)
 */
export const isStandardResponse = (
  response: any,
): response is StandardResponse => {
  return (
    typeof response === "object" &&
    response !== null &&
    "status" in response &&
    ("data" in response || "error" in response) &&
    (response.data === null) !== (response.error === null)
  );
};

/**
 * Helper to safely cast Redux thunk payload to StandardResponse type
 * Use this in component handlers to properly type the response.payload
 *
 * @example
 * const payload = getPayload(response.payload);
 * payload.error?.message  // TypeScript knows this is safe
 */
export const getPayload = <T = any>(payload: unknown): StandardResponse<T> => {
  return payload as StandardResponse<T>;
};
